import * as React from 'react';
import styled from 'styled-components';
import { forDesktop } from '@westwing/ui-kit/breakPoints';
import Benefits from './Benefits';
import { usePriceTiersBlock } from '@ids/context';
import ActionButton from '@ids/components/ActionButton/ActionButton';
import Heading from '@ids/components/Typography/Heading';
import { useIdsGaTracking, useQuestionnaireFlow } from '@ids/hooks';
import CTAPush from '@ids/components/CTAPush';
import { useFeatureToggle } from '@ids/appshell/hooks';
import { SerializedPriceTier } from '@ids/types/now-api';
import { Price } from '@ids/pages/landing/blocks/PriceTiers/Price';

const StyledMostPopularLabel = styled.div`
    position: absolute;
    top: 22px;
    right: -7px;
`;

const StyledPolygon = styled.div`
    position: relative;
    height: 32px;
    width: 98px;
`;

const StyledTriangle = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    height: calc(5px + 1%);
    width: 6.5px;
    clip-path: polygon(0% 0, 100% 100%, 0% 100%);
    background: #5f8b80;
`;

const StyledLabelBody = styled.span`
    position: absolute;
    bottom: 0;
    background: #5f8b80;
    width: 100%;
    height: 27px;
    clip-path: polygon(0% 100%, 100% 100%, 100% 0%, 0% 0%, 8% 50%);
`;

const StyledLabelText = styled.span`
    height: 27px;
    bottom: 0;
    left: 4px;
    display: grid;
    place-items: center;
    color: #fff;
    text-align: center;
    width: 100%;
    font-size: 12px;
    position: absolute;
`;

const MostPopularLabel = () => {
    const mostPopularText = usePriceTiersBlock()?.mostPopularText ?? 'Most Popular';

    return (
        <StyledMostPopularLabel>
            <StyledPolygon>
                <StyledTriangle />
                <StyledLabelBody />
                <StyledLabelText>{mostPopularText}</StyledLabelText>
            </StyledPolygon>
        </StyledMostPopularLabel>
    );
};

const StyledCards = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
`;

const StyledCard = styled.div`
    position: relative;
    width: min(325px, 100%);
    padding: 20px;
    margin-right: 12px;
    background: ${({ theme }) => theme.colors.surfaceCharcoal};
    border: 1px solid ${({ theme }) => theme.colors.charcoal20New};

    &[data-most-popular='true'] {
        background: ${({ theme }) => theme.colors.surfaceSuccess};
        border: 1px solid ${({ theme }) => theme.colors.surfaceSuccess};
    }

    ${forDesktop`
      margin-right: 20px;
    `}

    &:last-of-type {
        margin-right: 0;
    }
`;

const StyledTitle = styled(Heading).attrs({ as: 'h3' })`
    margin-bottom: 10px;
`;

const StyledCTAButton = styled(ActionButton)`
    height: 52px;
`;

const Cards = () => {
    const priceTiers = usePriceTiersBlock()?.priceTiers ?? [];
    const { startQuestionnaire } = useQuestionnaireFlow();
    const { trackIdsEvent } = useIdsGaTracking();
    const { p100StudioCTAPush } = useFeatureToggle();

    const onClick = (priceTier: SerializedPriceTier) => () => {
        trackIdsEvent({ action: 'Price Tiers', label: priceTier.trackingLabel });
        startQuestionnaire({
            surveySparrowConfig: priceTier.surveySparrowConfig,
            sku: priceTier.sku,
        });
    };

    return (
        <StyledCards data-testid="price-tiers-cards">
            {priceTiers.map((priceTier, idx) => (
                <StyledCard key={priceTier.title} data-most-popular={priceTier.mostPopular}>
                    {priceTier.mostPopular && <MostPopularLabel />}
                    <StyledTitle>{priceTier.title}</StyledTitle>
                    <Price priceTier={priceTier} />
                    <StyledCTAButton onClick={onClick(priceTier)} data-testid={`card-cta-price-${idx + 1}-button`}>
                        {priceTier.ctaText}
                    </StyledCTAButton>
                    {p100StudioCTAPush && priceTier.ctaPush ? (
                        <CTAPush {...priceTier.ctaPush} color="#5F8B80" shouldAnimate={false} />
                    ) : (
                        <CTAPush.Empty />
                    )}
                    <Benefits benefits={priceTier.benefits} />
                </StyledCard>
            ))}
        </StyledCards>
    );
};

export default Cards;
